/**
 * Screening criteria editor (US-2), split out of ListingDetailPage.tsx's ListingFieldsPanel with its
 * own form state, save and warning surface. Jurisdiction warnings (BR-1a / US-LC-13) stay soft and
 * never block the save; this editor shows only the warnings its own save returned.
 *
 *   variant 'card'  Screening & details section: read-only criteria with Edit / Set Criteria.
 *   variant 'bare'  edit sheet or guide step: the form only; Save and Cancel call `onDone`.
 *   embedded        (bare only) hosted in the listing guide: no Save/Cancel of its own; the step's
 *                   "Save and continue" calls `handleRef.save()`, which writes only when the form
 *                   differs from what is saved (the criteria are optional and advisory).
 */

import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { SMOKING_POLICY_LABELS, getCriteriaDisplay } from '@resida/shared';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import {
  upsertScreeningCriteria,
  type ConstraintWarning,
  type Listing,
  type ScreeningCriteria,
} from '../../../lib/leasing.api';
import type { GuideStepHandle } from '../listing-guide/guide-steps';
import { formatRentCents, useListingConstraints, WarningBanner } from './shared';

const smallInputCls =
  'w-full min-h-[44px] border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'block text-sm font-medium text-foreground mb-1';

/**
 * A yes/no setting as one 44px pill that reads its own state ("Accepted" / "Not accepted"). The whole
 * pill is the hit target; it is a `switch` so assistive tech announces on/off.
 */
function TogglePill({
  id,
  'data-testid': testId,
  checked,
  onChange,
  onLabel,
  offLabel,
  describedBy,
  labelledBy,
}: {
  id: string;
  'data-testid': string;
  checked: boolean;
  onChange: (next: boolean) => void;
  onLabel: string;
  offLabel: string;
  describedBy?: string;
  labelledBy?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-describedby={describedBy}
      aria-labelledby={labelledBy}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border px-5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        checked
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-input bg-background text-muted-foreground hover:bg-muted/50'
      )}
    >
      {checked ? onLabel : offLabel}
    </button>
  );
}

interface ScreeningCriteriaEditorProps {
  listing: Listing;
  screeningCriteria: ScreeningCriteria | null;
  onUpdated: () => void;
  variant?: 'card' | 'bare';
  /** Bare variant: called after a successful save (with no warnings to show) or on Cancel. */
  onDone?: () => void;
  /** Bare variant in a guide step: hide the form's own Save/Cancel (the step saves via `handleRef`). */
  embedded?: boolean;
  handleRef?: Ref<GuideStepHandle>;
  /** Fires when the unsaved-edits flag flips. */
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

/** The form's string/boolean state seeded from the saved criteria (or the BR-102 defaults when none). */
function initialFormValues(criteria: ScreeningCriteria | null) {
  return {
    minIncomeMultiple: criteria?.minIncomeMultiple != null ? String(criteria.minIncomeMultiple) : '',
    maxOccupants: criteria?.maxOccupants != null ? String(criteria.maxOccupants) : '',
    minCreditScore: criteria?.minCreditScore != null ? String(criteria.minCreditScore) : '',
    additionalCriteria: criteria?.additionalCriteria ?? '',
    smokingPolicy: (criteria?.smokingPolicy ?? 'prohibited') as string,
    acceptsHousingVouchers: criteria?.acceptsHousingVouchers ?? true,
    criminalLookbackYears:
      criteria?.criminalHistoryLookbackYears != null ? String(criteria.criminalHistoryLookbackYears) : '',
    // BR-102: publishing is on by default, including for a listing with no criteria row yet.
    publishScreeningCriteria: criteria?.publishScreeningCriteria ?? true,
  };
}

export function ScreeningCriteriaEditor({
  listing,
  screeningCriteria,
  onUpdated,
  variant = 'card',
  onDone,
  embedded = false,
  handleRef,
  onDirtyChange,
  onSavingChange,
}: ScreeningCriteriaEditorProps) {
  const isBare = variant === 'bare';
  const isClosed = listing.status === 'closed';
  const constraints = useListingConstraints(listing);

  const [editingCriteria, setEditingCriteria] = useState(isBare);
  const initial = initialFormValues(screeningCriteria);
  const [minIncomeMultiple, setMinIncomeMultiple] = useState(initial.minIncomeMultiple);
  const [maxOccupants, setMaxOccupants] = useState(initial.maxOccupants);
  const [minCreditScore, setMinCreditScore] = useState(initial.minCreditScore);
  const [additionalCriteria, setAdditionalCriteria] = useState(initial.additionalCriteria);
  const [smokingPolicy, setSmokingPolicy] = useState<string>(initial.smokingPolicy);
  const [acceptsHousingVouchers, setAcceptsHousingVouchers] = useState(initial.acceptsHousingVouchers);
  const [criminalLookbackYears, setCriminalLookbackYears] = useState(initial.criminalLookbackYears);
  const [publishScreeningCriteria, setPublishScreeningCriteria] = useState(initial.publishScreeningCriteria);
  const [criteriaError, setCriteriaError] = useState<string | null>(null);
  const [isSavingCriteria, setIsSavingCriteria] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState<ConstraintWarning[]>([]);

  const handleCancel = () => {
    setCriteriaError(null);
    if (isBare) {
      onDone?.();
      return;
    }
    setEditingCriteria(false);
  };

  /** Resolves with the soft warnings the save returned, or null when it failed (the error is shown). */
  const persist = async (): Promise<{ warnings: ConstraintWarning[] } | null> => {
    setCriteriaError(null);
    setSaveWarnings([]);
    setIsSavingCriteria(true);
    try {
      const result = await upsertScreeningCriteria(listing.id, {
        minIncomeMultiple: minIncomeMultiple ? parseFloat(minIncomeMultiple) : null,
        maxOccupants: maxOccupants ? parseInt(maxOccupants, 10) : null,
        minCreditScore: minCreditScore ? parseInt(minCreditScore, 10) : null,
        additionalCriteria: additionalCriteria || null,
        smokingPolicy: smokingPolicy as 'prohibited' | 'outdoor_only' | 'permitted',
        acceptsHousingVouchers,
        criminalHistoryLookbackYears: criminalLookbackYears ? parseInt(criminalLookbackYears, 10) : null,
        publishScreeningCriteria,
      });
      onUpdated();
      // Soft (BR-1a): saved; keep the warnings in view (a sheet stays open until Cancel).
      if (result.warnings.length > 0) setSaveWarnings(result.warnings);
      return { warnings: result.warnings };
    } catch (err) {
      setCriteriaError(err instanceof Error ? err.message : 'Failed to save criteria');
      return null;
    } finally {
      setIsSavingCriteria(false);
    }
  };

  const handleSaveCriteria = async (e: React.FormEvent) => {
    e.preventDefault();
    const saved = await persist();
    if (!saved) return;
    // Warnings keep a bare host open (the soft warning stays in view until Cancel).
    if (isBare) {
      if (saved.warnings.length === 0) onDone?.();
      return;
    }
    setEditingCriteria(false);
  };

  const dirty =
    minIncomeMultiple !== initial.minIncomeMultiple ||
    maxOccupants !== initial.maxOccupants ||
    minCreditScore !== initial.minCreditScore ||
    additionalCriteria !== initial.additionalCriteria ||
    smokingPolicy !== initial.smokingPolicy ||
    acceptsHousingVouchers !== initial.acceptsHousingVouchers ||
    criminalLookbackYears !== initial.criminalLookbackYears ||
    publishScreeningCriteria !== initial.publishScreeningCriteria;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    onSavingChange?.(isSavingCriteria);
  }, [isSavingCriteria, onSavingChange]);

  useImperativeHandle(handleRef, () => ({
    // Optional and advisory: an untouched form writes nothing.
    save: async () => (dirty ? (await persist()) !== null : true),
    isDirty: () => dirtyRef.current,
  }));

  const multiple = parseFloat(minIncomeMultiple);
  const incomeFigure =
    Number.isFinite(multiple) && multiple > 0 ? formatRentCents(Math.round(multiple * listing.askingRentCents)) : null;

  const criteriaDisplay = getCriteriaDisplay(screeningCriteria, listing.askingRentCents);

  const body = (
    <>
      {/* The guide's card already says what these are for; the live listing's section and its edit sheet need the sentence. */}
      {!embedded && (
        <p className="text-sm text-muted-foreground mb-3">
          Applicants see these criteria on their application form. When published, prospects also see
          them on your public listing.
        </p>
      )}

      {saveWarnings.length > 0 && <WarningBanner warnings={saveWarnings} />}

      {constraints?.screening.sourceOfIncomeProtected && (
        <p className="text-xs text-warning mb-3" data-testid="screening-criteria-source-of-income-note">
          Source-of-income discrimination is prohibited in {constraints.jurisdiction.displayName} — housing vouchers must be accepted.
        </p>
      )}

      {editingCriteria ? (
        <form onSubmit={(e) => void handleSaveCriteria(e)} className="space-y-4" data-testid="screening-criteria-form">
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
            <div>
              <label htmlFor="criteria-income-multiple" className={labelCls}>Minimum income</label>
              <input id="criteria-income-multiple" data-testid="screening-criteria-income-multiple" type="number" min="0" step="0.1"
                value={minIncomeMultiple} onChange={(e) => setMinIncomeMultiple(e.target.value)} placeholder="Not set" className={smallInputCls}
                aria-describedby="criteria-income-figure" />
              <p id="criteria-income-figure" data-testid="screening-criteria-income-figure" className="mt-1 text-xs text-muted-foreground">
                {incomeFigure
                  ? `${minIncomeMultiple}× monthly rent. About ${incomeFigure} a month at ${formatRentCents(listing.askingRentCents)} rent.`
                  : 'As a multiple of monthly rent, like 3.'}
              </p>
            </div>
            <div>
              <label htmlFor="criteria-max-occupants" className={labelCls}>Most occupants</label>
              <input id="criteria-max-occupants" data-testid="screening-criteria-max-occupants" type="number" min="1"
                value={maxOccupants} onChange={(e) => setMaxOccupants(e.target.value)} placeholder="Not set" className={smallInputCls} />
            </div>
            <div>
              <label htmlFor="criteria-min-credit" className={labelCls}>Minimum credit score</label>
              <input id="criteria-min-credit" data-testid="screening-criteria-min-credit" type="number" min="300" max="850"
                value={minCreditScore} onChange={(e) => setMinCreditScore(e.target.value)} placeholder="Not set" className={smallInputCls} />
            </div>

            <div>
              <label htmlFor="criteria-smoking-policy" className={labelCls}>Smoking</label>
              <select
                id="criteria-smoking-policy"
                data-testid="screening-smoking-policy"
                value={smokingPolicy}
                onChange={(e) => setSmokingPolicy(e.target.value)}
                className={smallInputCls}
              >
                <option value="prohibited">{SMOKING_POLICY_LABELS.prohibited}</option>
                <option value="outdoor_only">{SMOKING_POLICY_LABELS.outdoor_only}</option>
                <option value="permitted">{SMOKING_POLICY_LABELS.permitted}</option>
              </select>
            </div>
            <div>
              <label htmlFor="criteria-criminal-lookback" className={labelCls}>Criminal-history lookback</label>
              <input id="criteria-criminal-lookback" data-testid="screening-criteria-criminal-lookback" type="number" min="0" step="1"
                value={criminalLookbackYears} onChange={(e) => setCriminalLookbackYears(e.target.value)}
                placeholder="Not set" aria-describedby="criteria-criminal-lookback-help" className={smallInputCls} />
              <p id="criteria-criminal-lookback-help" className="text-xs text-muted-foreground mt-1">
                Years of history you consider. Leave empty if you don&apos;t use it.
              </p>
            </div>
            <div>
              <span id="criteria-vouchers-label" className={labelCls}>Housing vouchers</span>
              <TogglePill
                id="criteria-accepts-vouchers"
                data-testid="screening-criteria-vouchers-toggle"
                checked={acceptsHousingVouchers}
                onChange={setAcceptsHousingVouchers}
                onLabel="Accepted"
                offLabel="Not accepted"
                labelledBy="criteria-vouchers-label"
              />
            </div>
          </div>

          <div>
            <label htmlFor="criteria-additional" className={labelCls}>Anything else</label>
            <textarea id="criteria-additional" data-testid="screening-criteria-additional" value={additionalCriteria}
              onChange={(e) => setAdditionalCriteria(e.target.value)} rows={3}
              placeholder="For example: proof of income, references"
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y" />
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
            <div className="min-w-0">
              <span id="criteria-publish-label" className="block text-sm font-semibold text-foreground">Show these criteria on the listing</span>
              <p id="criteria-publish-help" className="text-xs text-muted-foreground mt-0.5">
                On by default. Some places require criteria to be disclosed in the listing itself. Applicants always see them on the application form.
              </p>
            </div>
            <TogglePill
              id="criteria-publish"
              data-testid="screening-criteria-publish-toggle"
              checked={publishScreeningCriteria}
              onChange={setPublishScreeningCriteria}
              onLabel="Shown"
              offLabel="Hidden"
              labelledBy="criteria-publish-label"
              describedBy="criteria-publish-help"
            />
          </div>

          {criteriaError && (
            <div data-testid="screening-criteria-error" className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm" role="alert">
              {criteriaError}
            </div>
          )}

          {!embedded && (
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="min-h-[44px]" disabled={isSavingCriteria} data-testid="screening-criteria-save">
                {isSavingCriteria ? 'Saving…' : 'Save Criteria'}
              </Button>
              <Button type="button" variant="outline" size="sm" className="min-h-[44px]" onClick={handleCancel} data-testid="screening-criteria-cancel" disabled={isSavingCriteria}>
                Cancel
              </Button>
            </div>
          )}
        </form>
      ) : screeningCriteria ? (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          {/* Same rows (labels and values) applicants and prospects see — one shared renderer. */}
          {criteriaDisplay.rows.map((row) => (
            <div key={row.key} data-testid={`screening-criteria-row-${row.key}`}>
              <dt className="text-muted-foreground text-xs">{row.label}</dt>
              <dd className="font-medium">{row.value}</dd>
            </div>
          ))}
          <div>
            <dt className="text-muted-foreground text-xs">In listings</dt>
            <dd className="font-medium" data-testid="screening-criteria-publish-value">
              {screeningCriteria.publishScreeningCriteria ? 'Published' : 'Not published'}
            </dd>
          </div>
          {criteriaDisplay.additionalCriteria && (
            <div className="col-span-2 sm:col-span-3">
              <dt className="text-muted-foreground text-xs">Additional</dt>
              <dd className="whitespace-pre-wrap">{criteriaDisplay.additionalCriteria}</dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground italic">No screening criteria set.</p>
      )}
    </>
  );

  if (isBare) {
    return <div data-testid="screening-criteria-section">{body}</div>;
  }

  return (
    <section
      data-testid="screening-criteria-section"
      className="mb-6 rounded-lg border border-border bg-card p-5"
      aria-labelledby="screening-criteria-title"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 id="screening-criteria-title" className="text-base font-semibold">Screening criteria</h2>
        {!isClosed && !editingCriteria && (
          <Button type="button" variant="outline" size="sm" className="min-h-[44px]" data-testid="screening-criteria-edit-button" onClick={() => setEditingCriteria(true)}>
            {screeningCriteria ? 'Edit' : 'Set Criteria'}
          </Button>
        )}
      </div>
      {body}
    </section>
  );
}
